/**
 * Custom Image Viewer - Advanced Gallery with Zoom & Pan
 * Features: Zoom, Pan, Touch gestures, Keyboard navigation, Responsive
 */

class CustomImageViewer extends HTMLElement {
    constructor() {
        super();
        
        // Core properties
        this.currentIndex = 0;
        this.images = [];
        this.isOpen = false;
        
        // Zoom properties
        this.zoomLevel = 1;
        this.minZoom = 0.5;
        this.maxZoom = 3;
        this.zoomStep = 0.2;
        
        // Pan properties
        this.isPanning = false;
        this.panStartX = 0;
        this.panStartY = 0;
        this.panCurrentX = 0;
        this.panCurrentY = 0;
        this.hasPanned = false; // Track if user has actually panned
        
        // Touch properties
        this.touchStartDistance = 0;
        this.touchStartZoom = 1;
        this.touches = [];
        
        // Elements
        this.overlay = null;
        this.imageEl = null;
        this.containerEl = null;
        
        // Animation tracking
        this.transitionTimeout = null;
        
        // Size tracking
        this.baseWidth = 0;
        this.baseHeight = 0;
        
        // Settings
        this.enableThumbs = true;
        this.sectionId = this.dataset.sectionId;
        
        // Event handlers (bound methods)
        this.handleKeydown = this.handleKeydown.bind(this);
        this.handleWheel = this.handleWheel.bind(this);
        this.handleResize = this.handleResize.bind(this);
    }
    
    connectedCallback() {
        this.init();
    }
    
    disconnectedCallback() {
        this.cleanup();
    }
    
    init() {
        this.overlay = this.querySelector('.custom-viewer-overlay');
        this.containerEl = this.querySelector('.custom-viewer-image-container');
        // imageEl will be set dynamically when images are created
        
        this.collectImages();
        this.bindEvents();
        this.setupTouchSupport();
        
        // Check if thumbnails are enabled
        this.enableThumbs = this.closest('[data-t4s-thumb-true]') !== null;
    }
    
    collectImages() {
        // Find the gallery container
        const galleryContainer = document.querySelector(`#shopify-section-${this.sectionId}`);
        if (!galleryContainer) return;
        
        // Get all gallery items
        const galleryItems = galleryContainer.querySelectorAll('.t4s-gallery-item');
        
        // Bind click events to gallery items
        galleryItems.forEach((item, index) => {
            item.addEventListener('click', (e) => {
                // Check if we should open viewer (not just go to link)
                const galleryAttr = item.closest('[data-custom-gallery]');
                if (galleryAttr) {
                    e.preventDefault();
                    e.stopPropagation();
                    this.openViewerForItem(item);
                }
            });
        });
    }
    
    collectImagesForItem(item) {
        // Get main image
        const mainImg = item.querySelector('img[data-custom-src]');
        if (!mainImg) return [];
        
        const images = [];
        
        // Add main image first
        images.push({
            index: 0,
            src: mainImg.dataset.customSrc,
            width: parseInt(mainImg.dataset.customW) || 800,
            height: parseInt(mainImg.dataset.customH) || 600,
            caption: mainImg.dataset.customCaption || '',
            description: mainImg.dataset.customDesc || '',
            buttonText: mainImg.dataset.customBtnText || '',
            buttonLink: mainImg.dataset.customBtnLink || '',
            alt: mainImg.alt || '',
            thumbSrc: mainImg.src || mainImg.dataset.src,
            isMain: true
        });
        
        // Get listing images from template
        const template = item.querySelector('template');
        if (template) {
            const listingContainer = template.content.querySelector('[listing-images]');
            if (listingContainer) {
                const listingImages = listingContainer.querySelectorAll('img');
                
                listingImages.forEach((img, index) => {
                    images.push({
                        index: index + 1,
                        src: img.src,
                        width: parseInt(img.dataset.customW) || parseInt(img.width) || 800,
                        height: parseInt(img.dataset.customH) || parseInt(img.height) || 600,
                        // Use main image's caption and button for all listing images
                        caption: mainImg.dataset.customCaption || '',
                        description: mainImg.dataset.customDesc || '',
                        buttonText: mainImg.dataset.customBtnText || '',
                        buttonLink: mainImg.dataset.customBtnLink || '',
                        alt: img.alt || '',
                        thumbSrc: img.src,
                        isMain: false
                    });
                });
            }
        }
        
        return images;
    }
    
    bindEvents() {
        // Close button
        const closeBtn = this.querySelector('.custom-viewer-close');
        if (closeBtn) {
            closeBtn.addEventListener('click', () => this.closeViewer());
        }
        
        // Navigation buttons
        const prevBtn = this.querySelector('.custom-viewer-prev');
        const nextBtn = this.querySelector('.custom-viewer-next');
        
        if (prevBtn) prevBtn.addEventListener('click', () => this.prevImage());
        if (nextBtn) nextBtn.addEventListener('click', () => this.nextImage());
        
        // Zoom controls
        const zoomInBtn = this.querySelector('.zoom-in');
        const zoomOutBtn = this.querySelector('.zoom-out');
        const zoomResetBtn = this.querySelector('.zoom-reset');
        
        if (zoomInBtn) zoomInBtn.addEventListener('click', () => this.zoomIn());
        if (zoomOutBtn) zoomOutBtn.addEventListener('click', () => this.zoomOut());
        if (zoomResetBtn) zoomResetBtn.addEventListener('click', () => this.resetZoom());
        
        // Overlay click to close
        if (this.overlay) {
            this.overlay.addEventListener('click', (e) => {
                if (e.target === this.overlay) {
                    this.closeViewer();
                }
            });
        }
        
        // Mouse events for panning - DISABLED
        // if (this.containerEl) {
        //     this.containerEl.addEventListener('mousedown', (e) => this.handleMouseDown(e));
        //     document.addEventListener('mousemove', (e) => this.handleMouseMove(e));
        //     document.addEventListener('mouseup', () => this.handleMouseUp());
        // }
        
        // Image events will be bound when images are created dynamically
    }
    
    setupTouchSupport() {
        if (!this.containerEl) return;
        
        this.containerEl.classList.add('touch-enabled');
        
        this.containerEl.addEventListener('touchstart', (e) => this.handleTouchStart(e), { passive: false });
        this.containerEl.addEventListener('touchmove', (e) => this.handleTouchMove(e), { passive: false });
        this.containerEl.addEventListener('touchend', (e) => this.handleTouchEnd(e), { passive: false });
    }
    
    openViewerForItem(item) {
        // Collect images for this specific item
        this.images = this.collectImagesForItem(item);
        
        if (this.images.length === 0) return;
        
        // Open viewer starting from first image (main image)
        this.openViewer(0);
    }
    
    openViewer(startIndex = 0) {
        if (this.images.length === 0) return;
        
        this.currentIndex = startIndex;
        this.isOpen = true;
        this.zoomLevel = 1;
        this.panCurrentX = 0;
        this.panCurrentY = 0;
        
        // Clear any existing transitions and set transform origin to center
        if (this.imageEl) {
            this.imageEl.style.transition = '';
            this.imageEl.style.transformOrigin = 'center center';
        }
        
        // Show overlay
        this.overlay.style.display = 'flex';
        document.body.style.overflow = 'hidden';
        
        // Add global event listeners
        document.addEventListener('keydown', this.handleKeydown);
        // document.addEventListener('wheel', this.handleWheel, { passive: false }); // Disabled wheel zoom
        window.addEventListener('resize', this.handleResize);
        
        // Create all image elements
        this.createImageElements();
        
        // Update viewer
        this.updateViewer();
        this.createThumbnails();
        
        // Focus for keyboard navigation
        this.overlay.focus();
    }
    
    closeViewer() {
        if (!this.isOpen) return;
        
        this.isOpen = false;
        
        // Add closing animation
        this.overlay.classList.add('closing');
        
        setTimeout(() => {
            this.overlay.style.display = 'none';
            this.overlay.classList.remove('closing');
            document.body.style.overflow = '';
        }, 300);
        
        // Remove global event listeners
        document.removeEventListener('keydown', this.handleKeydown);
        // document.removeEventListener('wheel', this.handleWheel); // Disabled wheel zoom
        window.removeEventListener('resize', this.handleResize);
    }
    
    updateViewer() {
        const current = this.images[this.currentIndex];
        if (!current) return;
        
        // Show current image, hide others
        this.showCurrentImage();
        
        // Update counter
        const currentEl = this.querySelector('.current');
        const totalEl = this.querySelector('.total');
        if (currentEl) currentEl.textContent = this.currentIndex + 1;
        if (totalEl) totalEl.textContent = this.images.length;
        
        // Update caption
        this.updateCaption(current);
        
        // Update thumbnails
        this.updateThumbnails();
        
        // Reset zoom and pan without animation for image change
        this.resetZoomInstant();
    }
    
    createImageElements() {
        // Clear existing images
        const existingImages = this.containerEl.querySelectorAll('.viewer-image-item');
        existingImages.forEach(img => img.remove());
        
        // Create image elements for all images
        this.images.forEach((imageData, index) => {
            const imgWrapper = document.createElement('div');
            imgWrapper.className = 'viewer-image-item';
            if (index === 0) {
                imgWrapper.classList.add('active');
                imgWrapper.style.display = 'flex';
            } else {
                imgWrapper.style.display = 'none';
            }
            imgWrapper.dataset.index = index;
            
            const img = document.createElement('img');
            img.className = 'custom-viewer-image';
            img.src = imageData.src;
            img.alt = imageData.alt;
            img.draggable = false;
            img.style.transformOrigin = 'center center';
            
            imgWrapper.appendChild(img);
            this.containerEl.appendChild(imgWrapper);
            
            // Bind events for each image
            img.addEventListener('contextmenu', (e) => e.preventDefault());
            img.addEventListener('click', (e) => this.handleImageClick(e));
            img.addEventListener('dblclick', (e) => this.handleImageDoubleClick(e));
        });
        
        // Update imageEl reference to current image
        this.updateImageReference();
    }
    
    showCurrentImage() {
        const imageItems = this.containerEl.querySelectorAll('.viewer-image-item');
        
        imageItems.forEach((item, index) => {
            if (index === this.currentIndex) {
                item.style.display = 'flex';
                // Use setTimeout to trigger fade in after display change
                setTimeout(() => item.classList.add('active'), 10);
            } else {
                item.classList.remove('active');
                // Hide after fade out completes
                setTimeout(() => {
                    if (!item.classList.contains('active')) {
                        item.style.display = 'none';
                    }
                }, 300);
            }
        });
        
        // Update imageEl reference
        this.updateImageReference();
        
        // Calculate size for current image
        this.calculateImageSize();
    }
    
    updateImageReference() {
        // Update imageEl to point to current active image
        const currentImageItem = this.containerEl.querySelector(`[data-index="${this.currentIndex}"]`);
        if (currentImageItem) {
            this.imageEl = currentImageItem.querySelector('.custom-viewer-image');
        }
    }
    
    updateCaption(imageData) {
        const captionEl = this.querySelector('.custom-viewer-caption');
        const titleEl = this.querySelector('.caption-title');
        const descEl = this.querySelector('.caption-desc');
        const btnEl = this.querySelector('.custom-viewer-btn');
        
        if (!captionEl) return;
        
        let hasContent = false;
        
        // Always use caption/button from main image (first image in the set)
        // All images in this popup share the same caption and button
        const mainImageData = this.images.length > 0 ? this.images[0] : imageData;
        
        // Update title
        if (titleEl) {
            if (mainImageData.caption) {
                titleEl.textContent = mainImageData.caption;
                titleEl.style.display = 'block';
                hasContent = true;
            } else {
                titleEl.style.display = 'none';
            }
        }
        
        // Update description
        if (descEl) {
            if (mainImageData.description) {
                descEl.innerHTML = mainImageData.description;
                descEl.style.display = 'block';
                hasContent = true;
            } else {
                descEl.style.display = 'none';
            }
        }
        
        // Update button
        if (btnEl) {
            if (mainImageData.buttonText && mainImageData.buttonLink) {
                btnEl.querySelector('.btn-text').textContent = mainImageData.buttonText;
                btnEl.href = mainImageData.buttonLink;
                btnEl.style.display = 'inline-block';
                hasContent = true;
            } else {
                btnEl.style.display = 'none';
            }
        }
        
        // Show/hide caption container
        // captionEl.style.display = hasContent ? 'block' : 'none';
    }
    
    createThumbnails() {
        if (!this.enableThumbs || this.images.length <= 1) return;
        
        const thumbsContainer = this.querySelector('.custom-viewer-thumbs');
        if (!thumbsContainer) return;
        
        thumbsContainer.innerHTML = '';
        
        this.images.forEach((imageData, index) => {
            const thumbEl = document.createElement('div');
            thumbEl.className = 'thumb-item';
            if (imageData.isMain) {
                thumbEl.classList.add('main-thumb');
            }
            thumbEl.innerHTML = `<img src="${imageData.thumbSrc}" alt="${imageData.alt}">`;
            
            thumbEl.addEventListener('click', () => {
                this.currentIndex = index;
                this.updateViewer();
            });
            
            thumbsContainer.appendChild(thumbEl);
        });
    }
    
    updateThumbnails() {
        const thumbItems = this.querySelectorAll('.thumb-item');
        thumbItems.forEach((thumb, index) => {
            thumb.classList.toggle('active', index === this.currentIndex);
        });
    }
    
    nextImage() {
        this.currentIndex = (this.currentIndex + 1) % this.images.length;
        this.updateViewer();
    }
    
    prevImage() {
        this.currentIndex = this.currentIndex === 0 ? 
            this.images.length - 1 : this.currentIndex - 1;
        this.updateViewer();
    }
    
    // Zoom Methods
    zoomIn() {
        this.zoomLevel = Math.min(this.maxZoom, this.zoomLevel + this.zoomStep);
        // Keep transform origin at center
        if (this.imageEl) {
            this.imageEl.style.transformOrigin = 'center center';
        }
        this.applyZoom(true); // With animation
    }
    
    zoomOut() {
        this.zoomLevel = Math.max(this.minZoom, this.zoomLevel - this.zoomStep);
        // Keep transform origin at center
        if (this.imageEl) {
            this.imageEl.style.transformOrigin = 'center center';
        }
        this.applyZoom(true); // With animation
    }
    
    resetZoom() {
        this.zoomLevel = 1;
        this.panCurrentX = 0;
        this.panCurrentY = 0;
        // Set transform origin to center
        if (this.imageEl) {
            this.imageEl.style.transformOrigin = 'center center';
        }
        this.applyZoom(true); // With animation
        this.calculateImageSize();
    }
    
    resetZoomInstant() {
        this.zoomLevel = 1;
        this.panCurrentX = 0;
        this.panCurrentY = 0;
        // Set transform origin to center
        if (this.imageEl) {
            this.imageEl.style.transformOrigin = 'center center';
        }
        this.applyZoom(false); // Without animation
        this.calculateImageSize();
    }
    
    applyZoom(withAnimation = false) {
        if (!this.imageEl || !this.containerEl) return;
        
        // Clear any existing timeout
        if (this.transitionTimeout) {
            clearTimeout(this.transitionTimeout);
            this.transitionTimeout = null;
        }
        
        // Add smooth transition if requested
        if (withAnimation) {
            this.imageEl.style.transition = 'transform 0.3s cubic-bezier(0.4, 0.0, 0.2, 1)';
            this.containerEl.style.transition = 'width 0.3s cubic-bezier(0.4, 0.0, 0.2, 1), height 0.3s cubic-bezier(0.4, 0.0, 0.2, 1)';
        } else {
            this.imageEl.style.transition = '';
            this.containerEl.style.transition = '';
        }
        
        const transform = `scale(${this.zoomLevel}) translate(${this.panCurrentX}px, ${this.panCurrentY}px)`;
        this.imageEl.style.transform = transform;
        
        // Update container size based on zoom level
        this.updateContainerSize();
        
        // Update zoom level display
        const zoomDisplay = this.querySelector('.zoom-level');
        if (zoomDisplay) {
            zoomDisplay.textContent = Math.round(this.zoomLevel * 100) + '%';
        }
        
        // Update cursor and zoom state (no panning)
        if (this.zoomLevel > 1) {
            this.imageEl.classList.add('zoomed');
            this.containerEl.classList.add('zoomed');
            this.imageEl.style.cursor = 'zoom-out';
        } else {
            this.imageEl.classList.remove('zoomed');
            this.containerEl.classList.remove('zoomed');
            this.imageEl.style.cursor = 'zoom-in';
        }
        
        // Remove transition after animation completes
        if (withAnimation) {
            this.transitionTimeout = setTimeout(() => {
                if (this.imageEl) {
                    this.imageEl.style.transition = '';
                }
                if (this.containerEl) {
                    this.containerEl.style.transition = '';
                }
                this.transitionTimeout = null;
            }, 300);
        }
    }
    
    calculateImageSize() {
        if (!this.imageEl || !this.images[this.currentIndex]) return;
        
        const current = this.images[this.currentIndex];
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        
        // Get responsive settings
        const settings = this.getResponsiveSettings();
        const maxWidth = viewportWidth * settings.maxWidthRatio;
        const maxHeight = viewportHeight * settings.maxHeightRatio;
        
        const imgRatio = current.width / current.height;
        const containerRatio = maxWidth / maxHeight;
        
        let finalWidth, finalHeight;
        
        if (imgRatio > containerRatio) {
            finalWidth = Math.min(maxWidth, current.width);
            finalHeight = finalWidth / imgRatio;
        } else {
            finalHeight = Math.min(maxHeight, current.height);
            finalWidth = finalHeight * imgRatio;
        }
        
        // Store base size for zoom calculations
        this.baseWidth = finalWidth;
        this.baseHeight = finalHeight;
        
        // Apply calculated size
        this.updateContainerSize();
    }
    
    updateContainerSize() {
        if (!this.containerEl || !this.baseWidth || !this.baseHeight) return;
        
        // Calculate container size based on zoom level
        const containerWidth = this.baseWidth * this.zoomLevel;
        const containerHeight = this.baseHeight * this.zoomLevel;
        
        // Apply size with viewport constraints
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const maxContainerWidth = viewportWidth * 0.95;
        const maxContainerHeight = viewportHeight * 0.85;
        
        const finalWidth = Math.min(containerWidth, maxContainerWidth);
        const finalHeight = Math.min(containerHeight, maxContainerHeight);
        
        this.containerEl.style.width = finalWidth + 'px';
        this.containerEl.style.height = finalHeight + 'px';
    }
    
    getResponsiveSettings() {
        const isMobile = window.innerWidth <= 768;
        const isTablet = window.innerWidth <= 1024;
        
        if (isMobile) {
            return {
                maxWidthRatio: 0.95,
                maxHeightRatio: 0.65,
                zoomStep: 0.3,
                minZoom: 0.8,
                maxZoom: 2
            };
        } else if (isTablet) {
            return {
                maxWidthRatio: 0.9,
                maxHeightRatio: 0.7,
                zoomStep: 0.25,
                minZoom: 0.6,
                maxZoom: 2.5
            };
        } else {
            return {
                maxWidthRatio: 0.85,
                maxHeightRatio: 0.75,
                zoomStep: 0.2,
                minZoom: 0.5,
                maxZoom: 3
            };
        }
    }
    
    // Mouse Event Handlers - DISABLED (No mouse panning)
    // handleMouseDown(e) {
    //     if (this.zoomLevel <= 1) return;
        
    //     e.preventDefault();
    //     this.isPanning = true;
    //     this.hasPanned = false; // Reset pan tracking
    //     this.panStartX = e.clientX - this.panCurrentX;
    //     this.panStartY = e.clientY - this.panCurrentY;
    //     this.imageEl.style.cursor = 'grabbing';
    // }
    
    // handleMouseMove(e) {
    //     if (!this.isPanning || this.zoomLevel <= 1) return;
        
    //     e.preventDefault();
        
    //     const newPanX = e.clientX - this.panStartX;
    //     const newPanY = e.clientY - this.panStartY;
        
    //     // Check if user has actually moved (threshold to distinguish from click)
    //     const moveThreshold = 5;
    //     if (Math.abs(newPanX - this.panCurrentX) > moveThreshold || 
    //         Math.abs(newPanY - this.panCurrentY) > moveThreshold) {
    //         this.hasPanned = true;
    //     }
        
    //     this.panCurrentX = newPanX;
    //     this.panCurrentY = newPanY;
    //     this.applyZoom();
    // }
    
    // handleMouseUp() {
    //     if (this.isPanning) {
    //         this.isPanning = false;
    //         if (this.imageEl) {
    //             this.imageEl.style.cursor = this.zoomLevel > 1 ? 'zoom-out' : 'zoom-in';
    //         }
    //     }
    // }
    
    // Touch Event Handlers
    handleTouchStart(e) {
        this.touches = Array.from(e.touches);
        
        if (this.touches.length === 1) {
            // Single touch - DISABLED panning
            // if (this.zoomLevel > 1) {
            //     e.preventDefault();
            //     this.isPanning = true;
            //     this.panStartX = this.touches[0].clientX - this.panCurrentX;
            //     this.panStartY = this.touches[0].clientY - this.panCurrentY;
            // }
        } else if (this.touches.length === 2) {
            // Two finger touch - start pinch zoom
            e.preventDefault();
            this.touchStartDistance = this.getTouchDistance(this.touches);
            this.touchStartZoom = this.zoomLevel;
        }
    }
    
    handleTouchMove(e) {
        this.touches = Array.from(e.touches);
        
        if (this.touches.length === 1 && this.isPanning) {
            // Single touch panning - DISABLED
            // e.preventDefault();
            // this.panCurrentX = this.touches[0].clientX - this.panStartX;
            // this.panCurrentY = this.touches[0].clientY - this.panStartY;
            // this.applyZoom();
        } else if (this.touches.length === 2) {
            // Pinch zoom
            e.preventDefault();
            const currentDistance = this.getTouchDistance(this.touches);
            const scale = currentDistance / this.touchStartDistance;
            const newZoom = Math.max(this.minZoom,Math.min(this.maxZoom, this.touchStartZoom * scale));
            
            if (newZoom !== this.zoomLevel) {
                this.zoomLevel = newZoom;
                // Reset pan when pinch zooming - no panning allowed
                this.panCurrentX = 0;
                this.panCurrentY = 0;
                this.applyZoom();
            }
        }
    }
    
    handleTouchEnd(e) {
        this.touches = Array.from(e.touches);
        
        if (this.touches.length === 0) {
            this.isPanning = false;
        }
    }
    
    getTouchDistance(touches) {
        if (touches.length < 2) return 0;
        const dx = touches[0].clientX - touches[1].clientX;
        const dy = touches[0].clientY - touches[1].clientY;
        return Math.sqrt(dx * dx + dy * dy);
    }
    
    // Keyboard Event Handler
    handleKeydown(e) {
        if (!this.isOpen) return;
        
        switch (e.key) {
            case 'Escape':
                e.preventDefault();
                this.closeViewer();
                break;
            case 'ArrowLeft':
                e.preventDefault();
                this.prevImage();
                break;
            case 'ArrowRight':
                e.preventDefault();
                this.nextImage();
                break;
            case '+':
            case '=':
                e.preventDefault();
                this.zoomIn();
                break;
            case '-':
                e.preventDefault();
                this.zoomOut();
                break;
            case '0':
                e.preventDefault();
                this.resetZoom();
                break;
        }
    }
    
    // Wheel Event Handler (Disabled but kept for reference)
    handleWheel(e) {
        if (!this.isOpen) return;
        
        e.preventDefault();
        
        const delta = e.deltaY > 0 ? -this.zoomStep : this.zoomStep;
        const newZoom = Math.max(this.minZoom, 
                        Math.min(this.maxZoom, this.zoomLevel + delta));
        
        if (newZoom !== this.zoomLevel) {
            this.zoomLevel = newZoom;
            // Always zoom to center
            if (this.imageEl) {
                this.imageEl.style.transformOrigin = 'center center';
            }
            this.applyZoom();
        }
    }
    
    
    // Image Click Handlers (PhotoSwipe-like behavior)
    handleImageClick(e) {
        // Disable on mobile
        if(window.innerWidth < 768) return;
        
        e.preventDefault();
        e.stopPropagation();
        
        // If zoomed out, zoom in to center
        if (this.zoomLevel <= 1) {
            this.zoomToCenter(1.5);
        } else {
            // If zoomed in, zoom out
            this.resetZoom();
        }
    }
    
    handleImageDoubleClick(e) {
        if(window.innerWidth < 768) return;
        e.preventDefault();
        e.stopPropagation();
        
        // Double click always resets zoom
        this.resetZoom();
    }
    
    zoomToCenter(targetZoom = 2) {
        if (!this.containerEl || !this.imageEl) return;
        
        // Set zoom level
        this.zoomLevel = Math.min(this.maxZoom, targetZoom);
        
        // Reset pan to center
        this.panCurrentX = 0;
        this.panCurrentY = 0;
        
        // Set transform origin to center
        this.imageEl.style.transformOrigin = 'center center';
        
        // Apply transform with smooth animation
        this.applyZoomWithAnimation();
    }
    
    applyZoomWithAnimation() {
        if (!this.imageEl || !this.containerEl) return;
        
        // Clear any existing timeout
        if (this.transitionTimeout) {
            clearTimeout(this.transitionTimeout);
        }
        
        // Add smooth transition
        this.imageEl.style.transition = 'transform 0.3s cubic-bezier(0.4, 0.0, 0.2, 1)';
        this.containerEl.style.transition = 'width 0.3s cubic-bezier(0.4, 0.0, 0.2, 1), height 0.3s cubic-bezier(0.4, 0.0, 0.2, 1)';
        
        // Apply transform
        const transform = `scale(${this.zoomLevel}) translate(${this.panCurrentX}px, ${this.panCurrentY}px)`;
        this.imageEl.style.transform = transform;
        
        // Update container size based on zoom level
        this.updateContainerSize();
        
        // Update zoom level display
        const zoomDisplay = this.querySelector('.zoom-level');
        if (zoomDisplay) {
            zoomDisplay.textContent = Math.round(this.zoomLevel * 100) + '%';
        }
        
        // Update cursor and pan state
        if (this.zoomLevel > 1) {
            this.imageEl.classList.add('zoomed');
            this.containerEl.classList.add('zoomed');
            this.imageEl.style.cursor = 'zoom-out';
        } else {
            this.imageEl.classList.remove('zoomed');
            this.containerEl.classList.remove('zoomed');
            this.imageEl.style.cursor = 'zoom-in';
        }
        
        // Remove transition after animation completes
        this.transitionTimeout = setTimeout(() => {
            if (this.imageEl) {
                this.imageEl.style.transition = '';
            }
            if (this.containerEl) {
                this.containerEl.style.transition = '';
            }
            this.transitionTimeout = null;
        }, 300);
    }
    
    // Resize Event Handler
    handleResize() {
        if (this.isOpen) {
            this.calculateImageSize();
        }
    }
    
    // Images are now preloaded when viewer opens, no need for separate preload
    
    cleanup() {
        document.removeEventListener('keydown', this.handleKeydown);
        // document.removeEventListener('wheel', this.handleWheel); // Disabled wheel zoom
        window.removeEventListener('resize', this.handleResize);
        
        // Clear any pending timeouts
        if (this.transitionTimeout) {
            clearTimeout(this.transitionTimeout);
            this.transitionTimeout = null;
        }
    }
}

// Register the custom element
customElements.define('custom-image-viewer', CustomImageViewer);

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
    // Auto-initialize all custom image viewers on the page
    const viewers = document.querySelectorAll('custom-image-viewer');
    viewers.forEach(viewer => {
        if (!viewer.initialized) {
            viewer.initialized = true;
        }
    });
});

// Export for potential external use
window.CustomImageViewer = CustomImageViewer;